import { FC, lazy, Suspense } from "react";

import LoadingSpinner from "@/components/elements/LoadingSpinner";
import CheckboxInput from "@/components/modules/FormFactory/FormCheckboxInput";
import CopyTextInput from "@/components/modules/FormFactory/FormCopyTextInput";
import FormTextInput from "@/components/modules/FormFactory/FormTextInput";
import { FormType } from "@/integrations";

import AppStoreConnectForm from "./AppStoreConnectForm";
import BigQueryForm from "./BigQueryForm";
import ChoiceForm from "./ChoiceForm";
import FacebookAdsForm from "./FacebookAdsForm/FacebookAdsForm";
import FormHiddenInput from "./FormHiddenInput";
import { FormRepeater } from "./FormRepeater";
import GoogleAdsForm from "./GoogleAdsForm/GoogleAdsForm";
import GoogleAnalytics4Form from "./GoogleAnalyticsForm/GoogleAnalytics4Form";
import { GoogleDriveForm } from "./GoogleDriveForm/GoogleDriveForm";
import InformationForm from "./InformationForm";
import KlaviyoForm from "./KlaviyoForm/KlaviyoForm";
import GorgiasForm from "./GorgiasForm/GorgiasForm";
import ShopifyForm from "./ShopifyForm/ShopifyForm";
import OAuthForm from "./OAuthForm";
import OpenWeatherForm from "./OpenWeatherForm/OpenWeatherForm";
import RedditAdsForm from "./RedditAdsForm/RedditAdsForm";
import SelectForm from "./SelectForm";
import SettingsResult from "./SettingsResult";
import SnowflakeForm from "./SnowflakeForm";
import { SubForm } from "./SubForm";
import FileUploadForm from "./FileUploadForm";
import { JsonFormRenderer } from "./JsonFormRenderer";

const GoogleAnalyticsForm = lazy(
  () => import("./GoogleAnalyticsForm/GoogleAnalyticsForm"),
);

interface FormFactoryProps {
  form: FormType;
  editMode?: boolean;
}

const FormFactory: FC<FormFactoryProps> = ({ form, editMode }) => {
  switch (form.type) {
    case "repeater":
      return <FormRepeater form={form} />;
    case "sub_form":
      return <SubForm form={form} editMode={editMode} />;
    case "text":
    case "email":
    case "password":
    case "number":
      return <FormTextInput field={form} />;
    case "hidden":
      return <FormHiddenInput field={form} />;
    case "copy":
      return <CopyTextInput field={form} />;
    case "checkbox":
      return <CheckboxInput field={form} />;
    case "big-query-form":
      return <BigQueryForm />;
    case "snowflake-form":
      return <SnowflakeForm />;
    case "dropdown":
      return <SelectForm field={form} />;
    case "choice":
      return <ChoiceForm field={form} editMode={editMode} />;
    case "information":
      return <InformationForm field={form} />;
    case "oauth":
      return <OAuthForm field={form} />;
    case "settings-result":
      return <SettingsResult field={form} />;
    case "google-analytics-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <GoogleAnalyticsForm field={form} />
        </Suspense>
      );
    case "google-analytics-4-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <GoogleAnalytics4Form field={form} />
        </Suspense>
      );
    case "facebook-ads-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <FacebookAdsForm field={form} />
        </Suspense>
      );
    case "google-ads-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <GoogleAdsForm field={form} />
        </Suspense>
      );
    case "reddit-ads-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <RedditAdsForm field={form} />
        </Suspense>
      );
    case "google-drive-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <GoogleDriveForm field={form} />
        </Suspense>
      );
    case "openweather-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <OpenWeatherForm field={form} />
        </Suspense>
      );
    case "klaviyo-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <KlaviyoForm field={form} />
        </Suspense>
      );
    case "gorgias-form":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <GorgiasForm field={form} />
        </Suspense>
      );
    case "shopify-custom-reports":
      return (
        <Suspense
          fallback={
            <div className="flex w-full justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <ShopifyForm field={form} />
        </Suspense>
      );
    case "app-store-connect-form":
      return <AppStoreConnectForm />;
    case "file-upload":
      return <FileUploadForm field={form} />;
    case "json-form":
      return <JsonFormRenderer field={form} />;
  }
};

export default FormFactory;
